/*
 * File: CommunityPanel.jsx
 * Project: our-wave-stories-app
 *
 * Created by Brendan Michaelsen on February 4, 2022 at 4:30 PM
 * Copyright © 2022 Our Wave, Inc. All rights reserved.
 *
 * Last Modified: June 4, 2025 at 12:47 PM
 * Modified By: Brendan Michaelsen
 */

/**
 * Imports
 */

// Modules
import React, {
	memo, useContext, useEffect, useMemo,
	useRef
} from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Trans, useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Parser, ProcessNodeDefinitions } from 'html-to-react';
import { useDebouncedCallback, useWindowResize } from 'beautiful-react-hooks';

// Context
import AnimationContextBuilder from '../../context/AnimationContext';

// Utilities
import { isValidNode, parseMarkdownLinks } from '../../utilities/parser';
import { formatNumber } from '../../../utilities/utilities';
import { renderPrimaryResource } from '../../utilities/resources';
import { topCenterPosition } from '../../utilities/position';

// Store
import { toggleGroundingExercise } from '../../store/slices/groundingExercise/groundingExercise.slice';

// Components
import { Typography } from '../Typography';
import { OnlineUsersWidget } from '../OnlineUsersWidget';
import { Button } from '../Button';
import { LocaleLink } from '../LocaleLink';
import { LocalitySelector } from '../LocalitySelector';

// Constants
import { AVAILABLE_I18N_NAMESPACES, DEFAULT_COUNTRY } from '../../../constants/base.constants';

// Styles
import * as S from './CommunityPanel.styles';


/**
 * Constants
 */

export const PANEL_VARIANTS = {
	HOME: 'home',
	LEARN: 'learn'
};


/**
 * Configuration
 */

// Parser
const ReactParser = new Parser();

// Node Definitions
const processNodeDefinitions = new ProcessNodeDefinitions();

// Parsing Instructions
const ParsingInstructions = [
	{
		shouldProcessNode(node) {
			return node.name === 'a';
		},
		processNode(node, children) {
			return <LocaleLink key={node.attribs.href} to={node.attribs.href} target="_blank">{children}</LocaleLink>;
		}
	},
	{
		shouldProcessNode() { return true; },
		processNode: processNodeDefinitions.processDefaultNode
	}
];


/**
 * Component
 */

export const CommunityPanel = memo(({
	className, variant, offset, hideBorder, inDrawer
}) => {

	// Get context from navigation parent
	const {
		dispatch: contextDispatch,
	} = useContext(AnimationContextBuilder);

	// Get current partner from hook
	const partner = useSelector((state) => state.partner.value);

	// Get current locale from hook
	const locale = useSelector((state) => state.locale.value);

	// Get platform metrics from hook
	const platformMetrics = useSelector((state) => state.metric.metrics);

	// Get resources manifest from hook
	const resourceManifest = useSelector((state) => state.resourceManifest.resources);

	// Get animation state
	const { reactionAnimationActive } = useSelector((state) => state.animation);

	// Create references for elements
	const isMounted = useRef(true);
	const reactionMetricRef = useRef();

	// Get actions from hooks
	const dispatch = useDispatch();

	// Get primary resource from manifest
	const primaryResource = useMemo(() => {
		if (resourceManifest && resourceManifest.length > 0) {
			const countryResource = resourceManifest.find((resource) => resource.reference_countries.includes(locale.countryId));
			if (countryResource) {
				return countryResource;
			}
			return resourceManifest.find((resource) => resource.reference_countries.includes(DEFAULT_COUNTRY)) || resourceManifest[0];
		}
		return null;
	}, [locale, resourceManifest]);

	// Get translation hook
	const [t] = useTranslation(AVAILABLE_I18N_NAMESPACES.CLIENT);

	// Get legal urls
	const termsUrl = useMemo(() => (partner?.legal?.links?.terms ? partner?.legal?.links?.terms : `https://www.ourwave.org${locale.localePath}/terms`), [partner, locale]);
	const privacyUrl = useMemo(() => (partner?.legal?.links?.privacy ? partner?.legal?.links?.privacy : `https://www.ourwave.org${locale.localePath}/privacy`), [partner, locale]);
	const communityGuidelinesUrl = useMemo(() => (partner?.legal?.links?.community_guidelines ? partner?.legal?.links?.community_guidelines : `https://www.ourwave.org${locale.localePath}/community-guidelines`), [partner, locale]);
	const hasInternalLegalPages = useMemo(() => partner?.legal?.has_internal_legal_pages, [partner]);

	// Get partner content
	const attributionContent = useMemo(() => partner?.content?.attribution, [partner]);
	const homePageTitle = useMemo(() => partner?.content?.pages?.home?.title, [partner]);
	const homePageDescription = useMemo(() => partner?.content?.pages?.home?.description, [partner]);
	const navigationMenuOptions = useMemo(() => partner?.navigation?.menu?.[locale.localeBaseId], [partner, locale]);

	// Open grounding activity
	const openGroundingActivity = () => {

		// Toggle grounding exercise
		dispatch(toggleGroundingExercise(true));
	};

	// Handle debounce function for updating context
	const debounceUpdateContext = useDebouncedCallback(() => {
		if (isMounted.current && !inDrawer) {

			// Get ref parameters
			const reactionRect = reactionMetricRef?.current?.getBoundingClientRect();

			// Set new position values
			contextDispatch({
				type: 'SET_DESKTOP_REACTION_METRIC_POSITION',
				payload: {
					left: reactionRect?.left || 0,
					top: topCenterPosition(reactionRect)
				}
			});
		}
	}, [inDrawer], 200);

	// Handle actions on window resize
	useWindowResize(() => {

		// Update context with debounce
		debounceUpdateContext();
	});

	// Handle actions on component load
	useEffect(() => {

		// Initialize mounted state
		isMounted.current = true;

		// Update context with debounce
		debounceUpdateContext();

		// Attach scroll listener
		if (isMounted.current) {
			document.addEventListener('scroll', () => {
				if (isMounted.current) {

					// Update context with debounce
					debounceUpdateContext();
				}
			}, true);
		}

		// Handle actions on dismount
		return () => { isMounted.current = false; };

	}, []);

	// Render primary resource
	const renderedPrimaryResource = useMemo(() => renderPrimaryResource(primaryResource, locale, t), [primaryResource, locale, t]);

	// Render component
	return (
		<S.Wrapper className={className} $offset={offset} $hideBorder={hideBorder}>

			{/* Section - Introduction */}
			{variant === PANEL_VARIANTS.HOME ? (
				<S.ContentSection>
					<Typography tag="h3" weight="black">
						{!homePageTitle ? (
							<Trans t={t} i18nKey="communityPanel.introductionTitle">
								Welcome to
								{' '}
								{{ partnerName: partner.name }}
								.
							</Trans>
						) : ReactParser.parseWithInstructions(parseMarkdownLinks(homePageTitle[locale.localeBaseId]), isValidNode, ParsingInstructions)}
					</Typography>
					<Typography tag="p">
						{!homePageDescription ? (
							<Trans t={t} i18nKey="communityPanel.introductionDescription">
								This is a space where survivors of trauma and abuse share their stories alongside supportive allies. These stories remind us that hope exists even in dark times. You are never alone in your experience. Healing is possible for everyone.
							</Trans>
						) : ReactParser.parseWithInstructions(parseMarkdownLinks(homePageDescription[locale.localeBaseId]), isValidNode, ParsingInstructions)}
					</Typography>
				</S.ContentSection>
			) : (
				<S.ContentSection>
					<Typography tag="p">
						Explore questions answered by experts to help survivors, advocates, and allies better understand trauma and the healing process.
					</Typography>
				</S.ContentSection>
			)}

			{/* Section - Metrics */}
			<S.MetricsSection>

				{/* Metrics Container */}
				<S.MetricContainer>

					{/* Metric - Members */}
					<S.Metric>
						<Typography tag="p" weight="semibold" className={platformMetrics.community_members_count == null && 'shimmer shimmer-element'}>{formatNumber(platformMetrics.community_members_count || 0)}</Typography>
						<Typography tag="p" variation="2" weight="regular">
							<Trans t={t} i18nKey="communityPanel.members">
								Members
							</Trans>
						</Typography>
					</S.Metric>

					{/* Metric - Views */}
					<S.Metric>
						<Typography tag="p" weight="semibold" className={platformMetrics.views_count == null && 'shimmer shimmer-element'}>{formatNumber(platformMetrics.views_count || 0)}</Typography>
						<Typography tag="p" variation="2" weight="regular">
							<Trans t={t} i18nKey="communityPanel.views">
								Views
							</Trans>
						</Typography>
					</S.Metric>

				</S.MetricContainer>
				<S.MetricContainer>

					{/* Metric - Reactions */}
					<S.Metric>
						<Typography
							tag="p"
							weight="semibold"
							className={platformMetrics.reaction_a_count == null && 'shimmer shimmer-element'}
							style={{ display: 'flex', alignItems: 'center' }}
							ref={reactionMetricRef}
						>
							{formatNumber(platformMetrics.reaction_a_count || 0)}
							{platformMetrics.reaction_a_count != null && (
								<S.ReactionBadge
									icon={['fas', 'heart']}
									className={reactionAnimationActive && !inDrawer ? 'animate animatePulse' : 'animate'}
								/>
							)}
						</Typography>
						<Typography tag="p" variation="2" weight="regular">
							<Trans t={t} i18nKey="communityPanel.reactionA">
								Reactions
							</Trans>
						</Typography>
					</S.Metric>

					{/* Metric - Reads */}
					{variant === PANEL_VARIANTS.HOME ? (
						<S.Metric>
							<Typography tag="p" weight="semibold" className={platformMetrics.story_views_count == null && 'shimmer shimmer-element'}>{formatNumber(platformMetrics.story_views_count || 0)}</Typography>
							<Typography tag="p" variation="2" weight="regular">
								<Trans t={t} i18nKey="communityPanel.storiesRead">
									Stories read
								</Trans>
							</Typography>
						</S.Metric>
					) : (
						<S.Metric>
							<Typography tag="p" weight="semibold" className={platformMetrics.question_views_count == null && 'shimmer shimmer-element'}>{formatNumber(platformMetrics.question_views_count || 0)}</Typography>
							<Typography tag="p" variation="2" weight="regular">
								<Trans t={t} i18nKey="communityPanel.questionsRead">
									Questions read
								</Trans>
							</Typography>
						</S.Metric>
					)}
				</S.MetricContainer>

				{/* Online Users */}
				<OnlineUsersWidget actionText={variant === PANEL_VARIANTS.HOME ? t('communityPanel.liveUsersStories') : t('communityPanel.liveUsersQuestions')} />

			</S.MetricsSection>

			{/* Section - Grounding Activity */}
			{/* <S.ContentSection>
				<Typography tag="h5" weight="semibold">
					<Trans t={t} i18nKey="communityPanel.groundingPrompt">
						Need to take a break?
					</Trans>
				</Typography>
				<Button
					variant="solid"
					variation="secondary"
					size="medium"
					style={{ margin: '10px 0px 0px' }}
					onClick={openGroundingActivity}
				>
					<Typography tag="span" weight="semibold">
						<Trans t={t} i18nKey="communityPanel.groundingButton">
							Try a grounding activity
						</Trans>
					</Typography>
				</Button>
			</S.ContentSection> */}

			{/* Section - Resources */}
			<S.ResourcesSection>

				{/* Primary Resource */}
				<S.PrimaryResource>
					{renderedPrimaryResource}
				</S.PrimaryResource>

				{/* Locality Selector */}
				<LocalitySelector forceCompact />

			</S.ResourcesSection>

			{/* Section - Footer */}
			<S.FooterSection>

				{/* Navigation Buttons */}
				{navigationMenuOptions && navigationMenuOptions.length && navigationMenuOptions.map((menuOption) => {
					if (menuOption.type === 'link') {
						return (
							<LocaleLink to={menuOption.url} target={menuOption.new_tab ? '_blank' : null} key={menuOption.text}>
								<Button
									variant="solid"
									variation="secondary"
									size="medium"
								>
									<Typography tag="span" weight="semibold">
										{menuOption.text}
									</Typography>
								</Button>
							</LocaleLink>
						);
					}
					return (
						<Button
							variant="solid"
							variation="secondary"
							size="medium"
							key={menuOption.text}
							onClick={menuOption.action}
						>
							<Typography tag="span" weight="semibold">
								{menuOption.text}
							</Typography>
						</Button>
					);
				})}

				{/* Attribution */}
				<Typography tag="p" variation="3">
					{!attributionContent ? (
						<Trans t={t} i18nKey="attribution">
							Made with
							<FontAwesomeIcon icon={['fas', 'heart']} className="heart" style={{ margin: '0px 4px' }} />
							in Raleigh, NC
						</Trans>
					) : ReactParser.parseWithInstructions(parseMarkdownLinks(attributionContent[locale.localeBaseId]), isValidNode, ParsingInstructions)}
				</Typography>

				{/* Legal */}
				<Typography tag="p" variation="3">
					<Trans t={t} i18nKey="legalFooter">
						Read our
						{' '}
						<LocaleLink to={communityGuidelinesUrl} target={!hasInternalLegalPages ? '_blank' : null}>Community Guidelines</LocaleLink>
						,
						{' '}
						<LocaleLink to={privacyUrl} target={!hasInternalLegalPages ? '_blank' : null}>Privacy Policy</LocaleLink>
						, and
						{' '}
						<LocaleLink to={termsUrl} target={!hasInternalLegalPages ? '_blank' : null}>Terms</LocaleLink>
					</Trans>
				</Typography>

			</S.FooterSection>
		</S.Wrapper>
	);
});


/**
 * Configuration
 */

CommunityPanel.displayName = 'CommunityPanel';
CommunityPanel.propTypes = {
	className: PropTypes.string,
	variant: PropTypes.string,
	offset: PropTypes.number,
	hideBorder: PropTypes.bool,
	inDrawer: PropTypes.bool
};
CommunityPanel.defaultProps = {
	className: null,
	variant: PANEL_VARIANTS.HOME,
	offset: 0,
	hideBorder: false,
	inDrawer: false
};
